import { useId, useMemo, useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download, FileUp, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { parseGpc, type GpcStatement } from "@/lib/gpc/gpcParser";
import { localePath, makeTr, type LandingLocale } from "@/lib/landing/locale";
import { gpcToPohoda, type GpcToPohodaError } from "@/lib/tools/gpcToPohoda";
import { isWithinFileCap } from "@/lib/tools/limits";
import { track } from "@/lib/tracking/analytics";
import { cn } from "@/lib/utils";

/**
 * The free GPC → POHODA tool: a Fio GPC statement, the company's IČO and the
 * bank account's abbreviation in POHODA to an XML import of bank movements.
 * Everything runs in this tab; after consent one `tool_used` event per
 * download, with nothing from the file or the form.
 */

type FileState =
  | { kind: "idle" }
  | { kind: "error"; message: string }
  | { kind: "done"; fileName: string; statements: GpcStatement[] };

const ERRORS: Record<GpcToPohodaError, [string, string]> = {
  ico_invalid: [
    "IČO nesedí: má 8 číslic a kontrolní číslici. Zkontrolujte ho.",
    "The company ID (IČO) does not check out: it has 8 digits and a check digit.",
  ],
  account_invalid: [
    "Zkratka účtu má nejvýš 19 znaků, bez < > & a uvozovek.",
    "The account abbreviation has at most 19 characters, without < > & or quotes.",
  ],
  no_movements: ["Výpis neobsahuje žádné pohyby.", "The statement has no movements."],
};

export function GpcToPohodaConverter({ locale }: { locale: LandingLocale }) {
  const tr = makeTr(locale);
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<FileState>({ kind: "idle" });
  const [dragging, setDragging] = useState(false);
  const [ico, setIco] = useState("");
  const [account, setAccount] = useState("");
  const [icoTouched, setIcoTouched] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  const result = useMemo(
    () =>
      file.kind === "done" && ico.trim() ? gpcToPohoda(file.statements, { ico, account }) : null,
    [file, ico, account]
  );
  const error = result && "error" in result ? result.error : null;
  const ready = result && !("error" in result) ? result : null;

  async function readFile(picked: File) {
    if (!isWithinFileCap(picked.size)) {
      setFile({
        kind: "error",
        message: tr(
          "Soubor je na výpis GPC příliš velký (víc než 10 MB).",
          "The file is too large for a GPC statement (over 10 MB)."
        ),
      });
      return;
    }
    try {
      const statements = parseGpc(await picked.arrayBuffer());
      if (statements.length === 0) throw new Error("no statements");
      setDownloaded(false);
      setFile({ kind: "done", fileName: picked.name, statements });
    } catch {
      setFile({
        kind: "error",
        message: tr(
          "Tohle nevypadá jako výpis GPC (ABO). Zkontrolujte, že jste ve Fio stáhli výpis ve formátu GPC.",
          "This doesn't look like a GPC (ABO) statement. Check that you downloaded the statement from Fio in GPC format."
        ),
      });
    }
  }

  function onPick(event: ChangeEvent<HTMLInputElement>) {
    const picked = event.target.files?.[0];
    event.target.value = "";
    if (picked) void readFile(picked);
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    const picked = event.dataTransfer.files?.[0];
    if (picked) void readFile(picked);
  }

  function download() {
    if (!ready) return;
    const blob = new Blob([ready.bytes as Uint8Array<ArrayBuffer>], { type: "application/xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = ready.fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
    setDownloaded(true);
    track("tool_used", { tool: "gpc_to_pohoda", output: "xml" });
  }

  const movements =
    file.kind === "done" ? file.statements.reduce((n, s) => n + s.items.length, 0) : 0;
  const icoError = icoTouched && error === "ico_invalid";

  return (
    <div className="rounded-xl border bg-card p-5 sm:p-6">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={cn(
          "flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed px-4 py-10 text-center transition-colors",
          dragging && "border-[hsl(var(--action))] bg-primary/10 dark:border-primary"
        )}
      >
        <FileUp aria-hidden="true" className="size-6 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          {tr(
            "Přetáhněte sem výpis .gpc, .abo nebo .txt, nebo",
            "Drop a .gpc, .abo or .txt statement here, or"
          )}
        </p>
        <Button type="button" onClick={() => inputRef.current?.click()}>
          {tr("Vybrat soubor", "Choose a file")}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept=".gpc,.abo,.txt"
          className="sr-only"
          aria-label={tr("Výpis GPC (ABO)", "GPC (ABO) statement")}
          onChange={onPick}
        />
      </div>

      <div aria-live="polite">
        {file.kind === "error" && (
          <p className="mt-5 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm">
            {file.message}
          </p>
        )}
        {file.kind === "done" && (
          <p className="mt-4 text-sm">
            <span className="font-medium">{file.fileName}</span>
            {" · "}
            {tr(
              `výpisů: ${file.statements.length}, pohybů: ${movements}`,
              `statements: ${file.statements.length}, movements: ${movements}`
            )}
          </p>
        )}
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor={`${id}-ico`}>{tr("IČO vaší firmy", "Your company ID (IČO)")}</Label>
          <Input
            id={`${id}-ico`}
            value={ico}
            onChange={(e) => setIco(e.target.value)}
            onBlur={() => setIcoTouched(true)}
            inputMode="numeric"
            maxLength={10}
            placeholder="12345678"
            aria-invalid={icoError ? true : undefined}
            className={cn("mt-1.5", icoError && "border-destructive")}
          />
          <p
            className={cn("mt-1 text-xs", icoError ? "text-destructive" : "text-muted-foreground")}
          >
            {icoError
              ? tr(...ERRORS.ico_invalid)
              : tr(
                  "POHODA naimportuje data jen do firmy se stejným IČO.",
                  "POHODA imports the data only into the company with the same IČO."
                )}
          </p>
        </div>
        <div>
          <Label htmlFor={`${id}-account`}>
            {tr("Zkratka účtu v POHODĚ (nepovinné)", "Account abbreviation in POHODA (optional)")}
          </Label>
          <Input
            id={`${id}-account`}
            value={account}
            onChange={(e) => setAccount(e.target.value)}
            maxLength={19}
            placeholder="FIO"
            className="mt-1.5"
          />
          <p
            className={cn(
              "mt-1 text-xs",
              error === "account_invalid" ? "text-destructive" : "text-muted-foreground"
            )}
          >
            {error === "account_invalid"
              ? tr(...ERRORS.account_invalid)
              : tr(
                  "Jak se bankovní účet jmenuje v POHODĚ v agendě Bankovní účty.",
                  "What the bank account is called in POHODA under Bank accounts."
                )}
          </p>
        </div>
      </div>

      {error === "no_movements" && (
        <p className="mt-4 text-sm text-destructive">{tr(...ERRORS.no_movements)}</p>
      )}

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <Button type="button" disabled={!ready} onClick={download}>
          <Download aria-hidden="true" className="mr-2 size-4" />
          {tr("Stáhnout XML pro POHODU", "Download XML for POHODA")}
        </Button>
        {ready && (
          <p className="text-sm text-muted-foreground">
            {tr(
              `${ready.movements} pohybů · ${ready.fileName}`,
              `${ready.movements} movements · ${ready.fileName}`
            )}
          </p>
        )}
      </div>

      <p className="mt-5 flex items-start gap-2 text-sm text-muted-foreground">
        <ShieldCheck
          aria-hidden="true"
          className="mt-0.5 size-4 shrink-0 text-[hsl(var(--action))] dark:text-primary"
        />
        {tr(
          "Soubor se nikam neodesílá: převod proběhne jen ve vašem prohlížeči a nic se neukládá.",
          "The file is never sent anywhere: the conversion runs only in your browser and nothing is stored."
        )}
      </p>

      {downloaded && (
        <p className="mt-5 rounded-lg border bg-secondary/40 px-4 py-3 text-sm leading-relaxed">
          {tr(
            "Jak XML v POHODĚ naimportovat, ukazuje ",
            "How to import the XML in POHODA is shown in "
          )}
          <Link
            to={localePath(locale, "/pohoda-guide")}
            className="font-medium text-[hsl(var(--action))] underline-offset-4 hover:underline dark:text-primary"
          >
            {tr("návod krok za krokem", "the step-by-step guide")}
          </Link>
          {tr(
            ". A chcete platby z Fio i doklady posílat do POHODY každý měsíc automaticky? ",
            ". And want Fio payments and documents sent to POHODA every month automatically? "
          )}
          <Link
            to={localePath(locale, "/")}
            className="inline-flex items-center gap-1 font-medium text-[hsl(var(--action))] underline-offset-4 hover:underline dark:text-primary"
          >
            {tr("Podívejte se na Zettl", "Take a look at Zettl")}
            <ArrowRight aria-hidden="true" className="size-3.5" />
          </Link>
        </p>
      )}
    </div>
  );
}
